/**
 * Error Handling Module
 * Combines authentication errors and general error handling utilities
 */

// =============================================================================
// BASE ERROR TYPES AND INTERFACES
// =============================================================================

export interface ErrorResponse {
  success: false;
  error: string;
  details?: string;
  code?: string;
}

export interface SuccessResponse<T = unknown> {
  success: true;
  data: T;
  message?: string;
}

export type ApiResponse<T = unknown> = SuccessResponse<T> | ErrorResponse;

export enum ErrorType {
  VALIDATION = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  INTERNAL = 'INTERNAL_ERROR',
  NETWORK = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT_ERROR',
  AUTH = 'AUTH_ERROR'
}

// =============================================================================
// AUTHENTICATION ERROR HANDLING
// =============================================================================

export interface AuthError {
  code: string;
  i18nKey: string;
  severity: 'error' | 'warning' | 'info';
  category: 'auth' | 'network' | 'validation' | 'system';
  retryable: boolean;
  userAction?: string;
}

export const AUTH_ERROR_MAPPINGS: Record<string, AuthError> = {
  'auth/wrong-password': {
    code: 'auth/wrong-password',
    i18nKey: 'auth.errors.invalidCredentials',
    severity: 'error',
    category: 'auth',
    retryable: true,
    userAction: 'checkCredentials'
  },
  'auth/invalid-credential': {
    code: 'auth/invalid-credential',
    i18nKey: 'auth.errors.invalidCredentials',
    severity: 'error',
    category: 'auth',
    retryable: true,
    userAction: 'checkCredentials'
  },
  'auth/user-not-found': {
    code: 'auth/user-not-found',
    i18nKey: 'auth.errors.userNotFound',
    severity: 'error',
    category: 'auth',
    retryable: false,
    userAction: 'checkEmail'
  },
  'auth/invalid-email': {
    code: 'auth/invalid-email',
    i18nKey: 'auth.errors.invalidEmail',
    severity: 'error',
    category: 'validation',
    retryable: true,
    userAction: 'fixEmail'
  },
  'auth/user-disabled': {
    code: 'auth/user-disabled',
    i18nKey: 'auth.errors.userDisabled',
    severity: 'error',
    category: 'auth',
    retryable: false,
    userAction: 'contactSupport'
  },
  'auth/too-many-requests': {
    code: 'auth/too-many-requests',
    i18nKey: 'auth.errors.tooManyRequests',
    severity: 'warning',
    category: 'auth',
    retryable: true,
    userAction: 'waitAndRetry'
  },
  'auth/weak-password': {
    code: 'auth/weak-password',
    i18nKey: 'auth.errors.weakPassword',
    severity: 'error',
    category: 'validation',
    retryable: true,
    userAction: 'strengthenPassword'
  },
  'auth/email-already-in-use': {
    code: 'auth/email-already-in-use',
    i18nKey: 'auth.errors.emailInUse',
    severity: 'error',
    category: 'auth',
    retryable: false,
    userAction: 'useLogin'
  },
  'auth/network-request-failed': {
    code: 'auth/network-request-failed',
    i18nKey: 'auth.errors.networkError',
    severity: 'error',
    category: 'network',
    retryable: true,
    userAction: 'checkConnection'
  },
  'auth/timeout': {
    code: 'auth/timeout',
    i18nKey: 'auth.errors.timeout',
    severity: 'warning',
    category: 'network',
    retryable: true,
    userAction: 'retry'
  },
  'auth/multi-factor-auth-required': {
    code: 'auth/multi-factor-auth-required',
    i18nKey: 'auth.errors.mfaRequired',
    severity: 'info',
    category: 'auth',
    retryable: true,
    userAction: 'completeMFA'
  },
  'auth/invalid-custom-token': {
    code: 'auth/invalid-custom-token',
    i18nKey: 'auth.errors.invalidToken',
    severity: 'error',
    category: 'system',
    retryable: false,
    userAction: 'relogin'
  },
  'auth/unknown': {
    code: 'auth/unknown',
    i18nKey: 'auth.errors.unknown',
    severity: 'error',
    category: 'system',
    retryable: true,
    userAction: 'retry'
  },
  'auth/sample-data-user-not-found': {
    code: 'auth/sample-data-user-not-found',
    i18nKey: 'auth.errors.sampleDataUserNotFound',
    severity: 'error',
    category: 'auth',
    retryable: false,
    userAction: 'useSampleCredentials'
  },
  'auth/signup-failed': {
    code: 'auth/signup-failed',
    i18nKey: 'auth.errors.signupFailed',
    severity: 'error',
    category: 'auth',
    retryable: true,
    userAction: 'retry'
  }
};

export const DEFAULT_ERROR_MESSAGES: Record<string, string> = {
  'auth.errors.invalidCredentials': 'The email or password you entered is incorrect.',
  'auth.errors.userNotFound': 'No account found with this email address.',
  'auth.errors.invalidEmail': 'Please enter a valid email address.',
  'auth.errors.userDisabled': 'This account has been disabled.',
  'auth.errors.tooManyRequests': 'Too many failed login attempts. Please wait and try again.',
  'auth.errors.weakPassword': 'Password is too weak. Please choose a stronger password.',
  'auth.errors.emailInUse': 'An account with this email already exists.',
  'auth.errors.networkError': 'Network connection failed.',
  'auth.errors.timeout': 'The request timed out.',
  'auth.errors.invalidToken': 'Authentication token is invalid.',
  'auth.errors.unknown': 'An unexpected error occurred.',
  'auth.errors.mfaRequired': 'Multi-factor authentication is required.',
  'auth.errors.sampleDataUserNotFound': 'This user is not available in sample data mode. Please use one of the predefined sample users.',
  'auth.errors.signupFailed': 'Failed to create account. Please try again.',
  'auth.errors.emailAlreadyInUse': 'An account with this email already exists. Please use the login page.',
  'auth.actions.checkCredentials': 'Double-check your email and password',
  'auth.actions.checkEmail': 'Verify the email address is correct',
  'auth.actions.contactSupport': 'Contact support for assistance',
  'auth.actions.waitAndRetry': 'Wait a few minutes and try again',
  'auth.actions.retry': 'Try again',
  'auth.actions.relogin': 'Please log in again',
  'auth.actions.useSampleCredentials': 'Use one of the sample user accounts'
};

// =============================================================================
// API RESPONSE UTILITIES
// =============================================================================

export function createErrorResponse(
  error: string,
  type: ErrorType = ErrorType.INTERNAL,
  details?: string
): ErrorResponse {
  return {
    success: false,
    error,
    details,
    code: type
  };
}

export function createSuccessResponse<T>(
  data: T,
  message?: string
): SuccessResponse<T> {
  return {
    success: true,
    data,
    message
  };
}

// =============================================================================
// AUTHENTICATION ERROR HANDLERS
// =============================================================================

export function mapFirebaseError(firebaseError: { code?: string }): AuthError {
  const errorCode = firebaseError?.code || 'auth/unknown';
  return AUTH_ERROR_MAPPINGS[errorCode] || AUTH_ERROR_MAPPINGS['auth/unknown'];
}

export function getErrorMessage(error: AuthError, t?: (key: string) => string): string {
  if (t) {
    return t(error.i18nKey);
  }
  return DEFAULT_ERROR_MESSAGES[error.i18nKey] || DEFAULT_ERROR_MESSAGES['auth.errors.unknown'];
}

export function getUserActionMessage(error: AuthError, t?: (key: string) => string): string | undefined {
  if (!error.userAction) return undefined;
  
  const actionKey = `auth.actions.${error.userAction}`;
  
  if (t) {
    return t(actionKey);
  }
  
  return DEFAULT_ERROR_MESSAGES[actionKey];
}

export function formatErrorMessage(error: AuthError, t?: (key: string) => string): string {
  const message = getErrorMessage(error, t);
  const action = getUserActionMessage(error, t);
  
  return action ? `${message}\n\n${action}` : message;
}

export function handleAuthError(firebaseError: { code?: string }, t?: (key: string) => string): ErrorResponse {
  const authError = mapFirebaseError(firebaseError);
  const message = formatErrorMessage(authError, t);
  
  return createErrorResponse(message, ErrorType.AUTH, authError.code);
}

// =============================================================================
// GENERAL ERROR HANDLERS
// =============================================================================

export function handleValidationError(errors: string[], entityType: string = 'Entity'): ErrorResponse {
  return createErrorResponse(
    `${entityType} validation failed`,
    ErrorType.VALIDATION,
    errors.join('; ')
  );
}

export function handleNotFoundError(entityType: string, identifier?: string): ErrorResponse {
  const message = identifier 
    ? `${entityType} with ID '${identifier}' not found`
    : `${entityType} not found`;
    
  return createErrorResponse(message, ErrorType.NOT_FOUND);
}

export function handleUnauthorizedError(action?: string): ErrorResponse {
  const message = action ? `Unauthorized to ${action}` : 'Unauthorized access';
  return createErrorResponse(message, ErrorType.UNAUTHORIZED);
}

export function handleForbiddenError(resource?: string): ErrorResponse {
  const message = resource ? `Access forbidden to ${resource}` : 'Access forbidden';
  return createErrorResponse(message, ErrorType.FORBIDDEN);
}

export function handleConflictError(entityType: string, reason?: string): ErrorResponse {
  const message = reason ? `${entityType} conflict: ${reason}` : `${entityType} already exists`;
  return createErrorResponse(message, ErrorType.CONFLICT);
}

export function handleNetworkError(operation?: string): ErrorResponse {
  const message = operation ? `Network error during ${operation}` : 'Network error occurred';
  return createErrorResponse(message, ErrorType.NETWORK);
}

export function handleTimeoutError(operation?: string): ErrorResponse {
  const message = operation ? `Timeout during ${operation}` : 'Operation timed out';
  return createErrorResponse(message, ErrorType.TIMEOUT);
}

export function handleGenericError(error: unknown, context: string = 'Operation'): ErrorResponse {
  if (error instanceof Error) {
    if (error.message.includes('not found')) return handleNotFoundError(context);
    if (error.message.includes('unauthorized')) return handleUnauthorizedError(context);
    if (error.message.includes('forbidden')) return handleForbiddenError(context);
    if (error.message.includes('network')) return handleNetworkError(context);
    if (error.message.includes('timeout')) return handleTimeoutError(context);
    
    return createErrorResponse(`${context} failed`, ErrorType.INTERNAL, error.message);
  }
  
  return createErrorResponse(`${context} failed`, ErrorType.INTERNAL, 'Unknown error');
}

// =============================================================================
// ASYNC OPERATION UTILITIES
// =============================================================================

export async function handleAsyncOperation<T>(
  operation: () => Promise<T>,
  errorMessage: string = 'Operation failed'
): Promise<ApiResponse<T>> {
  try {
    const result = await operation();
    return createSuccessResponse(result);
  } catch (error) {
    console.error(`${errorMessage}:`, error);
    return handleGenericError(error, errorMessage);
  }
}

export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000,
  context: string = 'Operation'
): Promise<ApiResponse<T>> {
  let lastError: unknown;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return createSuccessResponse(result);
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  return handleGenericError(lastError, `${context} (after ${maxRetries} attempts)`);
}

// =============================================================================
// BATCH OPERATION UTILITIES
// =============================================================================

export interface BatchResult<T> {
  successful: T[];
  failed: Array<{ item: unknown; error: string; }>;
  totalProcessed: number;
  successCount: number;
  failureCount: number;
}

export async function handleBatchOperation<T, R>(
  items: T[],
  operation: (item: T) => Promise<R>,
  context: string = 'Batch operation'
): Promise<ApiResponse<BatchResult<R>>> {
  const result: BatchResult<R> = {
    successful: [],
    failed: [],
    totalProcessed: items.length,
    successCount: 0,
    failureCount: 0
  };
  
  for (const item of items) {
    try {
      const processedItem = await operation(item);
      result.successful.push(processedItem);
      result.successCount++;
    } catch (error) {
      result.failed.push({
        item,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      result.failureCount++;
    }
  }
  
  const message = `${context}: ${result.successCount} successful, ${result.failureCount} failed`;
  return createSuccessResponse(result, message);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function logError(error: unknown, context: string, data?: Record<string, unknown>): void {
  console.error('Error:', {
    context,
    timestamp: new Date().toISOString(),
    error: error instanceof Error ? { name: error.name, message: error.message } : error,
    ...data
  });
}

export function isErrorResponse(response: ApiResponse): response is ErrorResponse {
  return !response.success;
}

export function isSuccessResponse<T>(response: ApiResponse<T>): response is SuccessResponse<T> {
  return response.success;
}

export function extractDataOrThrow<T>(response: ApiResponse<T>): T {
  if (isErrorResponse(response)) {
    throw new Error(response.error);
  }
  return response.data;
}

export function extractDataSafely<T>(response: ApiResponse<T>, defaultValue: T): T {
  return isErrorResponse(response) ? defaultValue : response.data;
} 
